import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import HighlightableCard, { HighlightableCardRef } from '../shared/HighlightableCard'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams } from 'react-router-dom'

import RegistrationList from '../registrations/RegistrationList'
import PaymentDetails from '../payment/PaymentDetails'
import { RootState } from '../../store'
import { usePageTitle } from '../../hooks'
import { paymentTypes } from '../payment/PaymentTypeSelector'
import * as routes from '../../routes'
import StageNavigation from '../shared/StageNavigation'
import { addToast } from '../../store/misc/misc'
import { ToastType } from '../../store/misc/interfaces'
import ParticipantData from './ParticipantData'
import { EventModal } from '../events/EventDetailModal'
import { getRegisteredEvents } from '../../store/events/selectors'
import { getNewRegistrations } from '../../store/registrations/selectors'
import EnhancedRadioGroup, { EnhancedRadioGroupRef } from '../shared/EnhancedRadioGroup'
import { useCheckPaymentStageSkip } from '../../functions/registrations/checkPaymentStageSkip'
import LoadedViaTokenIndicator from '../shared/LoadedViaTokenIndicator'

type VerifyStageParams = {
  eventId: string
}

const VerifyStage = () => {
  const { eventId } = useParams<VerifyStageParams>()
  const dispatch = useDispatch()
  const { data: program } = useSelector((state: RootState) => state.program)
  const { data: participant } = useSelector(
    (state: RootState) => state.participant
  )
  const { list: registrations } = useSelector(
    (state: RootState) => state.registrations
  )
  const { method: selectedPaymentMethod } = useSelector(
    (state: RootState) => state.paymentData
  )
  const registeredEvents = useSelector(getRegisteredEvents)
  const newRegistrations = useSelector(getNewRegistrations)
  const { baseUrl } = useSelector((state: RootState) => state.misc)

  const selectedPayment = paymentTypes.find(
    (pType) => pType.code === selectedPaymentMethod
  )

  let navigate = useNavigate()

  const [DataProtectionChecked, setDataProtectionChecked] = useState(false)
  const [ConditionAgreementChecked, setConditionAgreementChecked] =
    useState(false)


  const { family_token } = useSelector(
    (state: RootState) => state.participant.data
  )

  const appliedDiscountCodes = registrations.reduce((acc, reg) => {
    reg.discountCodes.forEach((dc) => {
      if (!acc.includes(dc.code)) {
        acc.push(dc.code)
      }
    })
    return acc
  }, [] as string[])

  usePageTitle('Anmeldedaten überprüfen')

  const dataProtectionRef = useRef<HTMLInputElement | null>(null)
  const conditionsRef = useRef<HTMLInputElement | null>(null)
  const photosAllowedRef = useRef<EnhancedRadioGroupRef>(null)
  const newsletterAllowedRef = useRef<EnhancedRadioGroupRef>(null)
  const dataProtectionCardRef = useRef<HighlightableCardRef>(null)
  const conditionsCardRef = useRef<HighlightableCardRef>(null)

  const skipPaymentStage = useCheckPaymentStageSkip()

  return (
    <>
      {eventId && (
        <EventModal
          eventId={parseInt(eventId)}
          editable={false}
          events={registeredEvents}
        />
      )}
      <Card>
        <Card.Body>
          <Card.Title>
            <h2 aria-level={1}>Anmeldung überprüfen</h2>
            <LoadedViaTokenIndicator />
          </Card.Title>
          <Card.Text>Überprüfe alle bisher ausgefüllten Daten.</Card.Text>
          <Card.Text>Notwendige Felder sind mit einem * markiert.</Card.Text>
        </Card.Body>
      </Card>
      <br />
      <Card>
        <Card.Header>
          <Row>
            <Col md={6} sm={12}>
              <h5 className="mb-2 mb-sm-0">Persönliche Angaben</h5>
            </Col>
            {!participant.token && (
              <Col md={6} sm={12} className="d-flex flex-row-reverse">
                <Button
                  variant="primary"
                  className="mobile-btn dark-primary-background"
                  onClick={() => navigate(routes.PARTICIPANT.path)}
                >
                  <FontAwesomeIcon icon="edit" role="presentation" /> Angaben
                  bearbeiten
                </Button>
              </Col>
            )}
          </Row>
        </Card.Header>
        <Card.Body>
          <ParticipantData />
        </Card.Body>
      </Card>
      <br />
      <Card>
        <Card.Header>
          <Row>
            <Col md={6} sm={12}>
              <h5 className="mb-2 mb-sm-0">Ausgewählte Veranstaltungen</h5>
            </Col>
            <Col md={6} sm={12}>
              <Row className="w-100 m-0">
                <Col
                  lg={12}
                  xl={6}
                  className="d-flex justify-content-end pr-sm-0"
                >
                  <Button
                    variant="primary"
                    className="mobile-btn dark-primary-background"
                    onClick={() => navigate(routes.EVENTS.path)}
                  >
                    <FontAwesomeIcon icon="list" role="presentation" /> Auswahl
                    ändern
                  </Button>
                </Col>
                <Col
                  lg={12}
                  xl={6}
                  className="d-flex justify-content-end pr-sm-0"
                >
                  <Button
                    variant="primary"
                    className="mobile-btn dark-primary-background"
                    onClick={() => navigate(routes.REGISTRATIONS.path)}
                  >
                    <FontAwesomeIcon icon="edit" role="presentation" />{' '}
                    {registrations.length > 1 ? 'Anmeldungen ' : 'Anmeldung '}
                    anpassen
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <RegistrationList isEditable={false} />
        </Card.Body>
      </Card>
      <br />
      {!skipPaymentStage && (
        <Card>
          <Card.Header>
            <strong>Ausgewählte Zahlungsart:</strong>{' '}
            {selectedPayment ? selectedPayment.name : 'Externe Zahlung'}
          </Card.Header>
          <Card.Body>
            <PaymentDetails
              registrations={registrations}
              isVerifyStage={true}
            />
            {registrations.some((reg) => reg.event?.has_external_payment) && (
              <div className="mt-3">
                <strong>Wichtiger Hinweis:</strong> Einige ausgewählte
                Veranstaltungen erfordern eine externe Zahlung. Bitte
                die jeweiligen Zahlungsanweisungen des Veranstalters beachten.
              </div>
            )}
          </Card.Body>
        </Card>
      )}
      <br />
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Rechtliches</h5>{' '}
        </Card.Header>
        <Card.Body>
          <HighlightableCard ref={dataProtectionCardRef} className="mb-3">
            <Card.Header>
              <strong>Hinweise zur Datenschutzerklärung*</strong>
            </Card.Header>
            <Card.Body>
              <p>
                Der Schutz der Privatsphäre ist für uns sehr wichtig. In
                unserer{' '}
                <a
                  href={`${baseUrl}/datenschutz`}
                  className="font-weight-bold dark-primary-text"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>{' '}
                informieren wir ausführlich über die Verarbeitung der
                Daten bei einer Anmeldung über
                Feripro.
              </p>
              <p>
                Die eingegebenen und oben angezeigten personenbezogenen
                Daten werden ausschließlich für die Erfüllung des zwischen uns
                abgeschlossenen Vertrags verarbeitet. Wir können die Weitergabe
                an einen oder mehrere Auftragsverarbeiter (z.B. Teilnehmerlisten
                an Veranstalter) veranlassen, welche die Daten zur Erfüllung
                des zwischen uns geschlossenen Vertrags und unter unserer
                Kontrolle verarbeiten und nutzen. Eine sonstige Weitergabe
                der Daten an Dritte erfolgt grundsätzlich nicht, sofern diese
                nicht der Erfüllung des zwischen uns abgeschlossenen Vertrags
                dient, keine gesetzliche Pflicht zur Weitergabe besteht, die
                Weitergabe der Strafverfolgung dient oder vorher
                eingewilligt wurde.
              </p>
              <p>
                Um eine möglichst angenehme Nutzung unseres Dienstes zu
                ermöglichen, werden die eingegebenen Daten in einem
                technisch notwendigen Cookie gespeichert, um diese bei
                späteren Nachbuchungen oder Anpassungen wieder zu verwenden.
                Weitere Informationen zum Einsatz von Cookies befinden sich in der{' '}
                <a
                  href={`${baseUrl}/datenschutz`}
                  className="font-weight-bold dark-primary-text"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>
                . Die Daten werden entsprechend der Datenschutzerklärung (siehe
                6.) nach Erreichen des Zwecks wieder gelöscht.
              </p>
              <br />
              <Form.Check
                type="checkbox"
                name="dataProtection"
                id="dataProtection"
                ref={dataProtectionRef}
                label={[
                  'Ich nehme die ',
                  <a
                    key={'data-link-1'}
                    href={`${baseUrl}/datenschutz`}
                    className="font-weight-bold dark-primary-text"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Datenschutzerklärung
                  </a>,
                  ' zur Kenntnis.',
                ]}
                onChange={() =>
                  setDataProtectionChecked(!DataProtectionChecked)
                }
              />
            </Card.Body>
          </HighlightableCard>
          {program.conditions_html && (
            <HighlightableCard className="mt-3" ref={conditionsCardRef}>
              <Card.Header>
                <strong>Teilnahmebedingungen*</strong>
              </Card.Header>
              <Card.Body>
                <p
                  dangerouslySetInnerHTML={{ __html: program.conditions_html }}
                />
                <br />
                <Form.Check
                  ref={conditionsRef}
                  type="checkbox"
                  name="conditionAgreement"
                  id="conditionAgreement"
                  label="Ich willige in die Teilnahmebedingungen ein."
                  onChange={() =>
                    setConditionAgreementChecked(!ConditionAgreementChecked)
                  }
                />
              </Card.Body>
            </HighlightableCard>
          )}
          {!participant.token && program.ask_for_photo_permission ? (
            <Card className="mt-3">
              <Card.Header>
                <strong>Einwilligung zur Veröffentlichung von Fotos</strong>
              </Card.Header>
              <Card.Body>
                <p
                  dangerouslySetInnerHTML={{
                    __html: program.ask_for_photo_text_html,
                  }}
                />
                <br />
                <EnhancedRadioGroup
                  ref={photosAllowedRef}
                  name="photos_allowed"
                  participant={participant}
                  required={true}
                  options={[
                    {
                      value: true,
                      label: 'Ich stimme zu',
                      icon: 'thumbs-up',
                      colorClass: 'success'
                    },
                    {
                      value: false,
                      label: 'Ich stimme NICHT zu',
                      icon: 'thumbs-down',
                      colorClass: 'danger'
                    }
                  ]}
                />
              </Card.Body>
            </Card>
          ) : (
            <></>
          )}
          {!participant.token && program.agreement_html ? (
            <HighlightableCard className="mt-3">
              <Card.Header>
                <strong>Einverständnis Kontaktaufnahme</strong>
              </Card.Header>
              <Card.Body>
                <p
                  dangerouslySetInnerHTML={{ __html: program.agreement_html }}
                />
                <br />
                <EnhancedRadioGroup
                  ref={newsletterAllowedRef}
                  name="newsletter_allowed"
                  participant={participant}
                  options={[
                    {
                      value: true,
                      label: 'Ja, ich möchte kontaktiert werden',
                      icon: 'check',
                      colorClass: 'success'
                    },
                    {
                      value: false,
                      label: 'Nein, bitte nicht kontaktieren',
                      icon: 'times',
                      colorClass: 'danger'
                    }
                  ]}
                />
              </Card.Body>
            </HighlightableCard>
          ) : (
            <></>
          )}
        </Card.Body>
      </Card>
      <StageNavigation
        backward={{
          text: 'Zurück',
          handler: () => {
            if (skipPaymentStage) {
              navigate(routes.PARTICIPANT.path)
            } else {
              navigate(routes.PAYMENT.path)
            }
          },
        }}
        forward={{
          text: routes.SUCCESS.longName,
          icon: 'check',
          verifyClass: 'btn-success',
          handler: async () => {
            if (newRegistrations.length === 0) {
              dispatch(
                addToast({
                  title: 'Fehler',
                  body: 'Ihre Anmeldung enthält keine neuen Veranstaltungen! Sie haben sich bereits für alle ausgewählten Veranstaltungen angemeldet. Bitte wählen Sie andere Veranstaltungen aus oder kontaktieren Sie den Support.',
                  type: ToastType.Danger,
                })
              )
            } else if (!DataProtectionChecked) {
              dataProtectionCardRef.current?.scrollIntoView()
              dataProtectionCardRef.current?.triggerHighlight()
              dispatch(
                addToast({
                  title: 'Fehler',
                  body: 'Bitte den Hinweisen zur Datenschutzerklärung zustimmen.',
                  type: ToastType.Danger,
                })
              )
            } else if (program.conditions_html && !ConditionAgreementChecked) {
              conditionsCardRef.current?.scrollIntoView()
              conditionsCardRef.current?.triggerHighlight()
              dispatch(
                addToast({
                  title: 'Fehler',
                  body: 'Bitte den Teilnahmebedingungen zustimmen.',
                  type: ToastType.Danger,
                })
              )
            } else if (
              !participant.token &&
              program.ask_for_photo_permission &&
              typeof participant.photos_allowed !== 'boolean'
            ) {
              photosAllowedRef.current?.scrollIntoView()
              photosAllowedRef.current?.triggerAnimation()
              dispatch(
                addToast({
                  title: 'Fehler',
                  body: 'Bitte auswählen, ob der Veröffentlichung von Fotos zugestimmt wird oder nicht.',
                  type: ToastType.Danger,
                })
              )
            } else {
              navigate(routes.SUCCESS.path)
            }
          },
        }}
      />
    </>
  )
}

export default VerifyStage
